import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import "../styles/CaseStudy.css";
import Footer from "../components/Footer";
import { db } from "../firebase";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore";

const CaseStudy = () => {
  const { slug } = useParams();

  const [caseStudy, setCaseStudy] = useState(null);
  const [allCaseStudies, setAllCaseStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const itemsRef = collection(db, "pageContent", "caseStudies", "items");
    const q = query(itemsRef, orderBy("order", "asc"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const items = snapshot.docs
          .map((item) => ({
            id: item.id,
            ...item.data(),
          }))
          .filter((item) => item.published !== false);

        setAllCaseStudies(items);

        const currentCaseStudy = items.find(
          (item) => item.slug === slug
        );

        setCaseStudy(currentCaseStudy || null);
        setLoading(false);
      },
      (err) => {
        console.error("Error fetching case study:", err);
        setError("Failed to load case study.");
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [slug]);

  if (loading) {
    return (
      <div className="case-study-page case-study-state">
        <p>Loading case study...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="case-study-page case-study-state">
        <p>{error}</p>
      </div>
    );
  }

  if (!caseStudy) {
    return (
      <div className="case-study-page case-study-state">
        <h1>Case Study Not Found</h1>
        <Link to="/case-studies">Back to Case Studies</Link>
      </div>
    );
  }

  const gallery = Array.isArray(caseStudy.gallery)
    ? caseStudy.gallery.filter(Boolean)
    : [];

  const tools = caseStudy.tools
    ? caseStudy.tools
        .split(",")
        .map((tool) => tool.trim())
        .filter(Boolean)
    : [];

  const hasOverview = Boolean(caseStudy.description);
  const hasRole = Boolean(caseStudy.role);
  const hasDeliverables = Boolean(caseStudy.deliverables);
  const hasTimeline = Boolean(caseStudy.timeline);
  const hasTools = tools.length > 0;
  const hasLiveUrl = Boolean(caseStudy.liveUrl);

  const otherCaseStudies = allCaseStudies.filter(
    (item) => item.id !== caseStudy.id
  );

  return (
    <div className="case-study-page">

      {/* =========================
          Hero
          ========================= */}

      <section className="case-study-hero">
        <div className="case-study-hero-overlay" />

        <div className="case-study-hero-content">
          {caseStudy.category && (
            <span className="case-study-hero-category">
              {caseStudy.category}
            </span>
          )}

          <h1>{caseStudy.title}</h1>

          {caseStudy.description && (
            <p>{caseStudy.description}</p>
          )}
        </div>
      </section>

      {/* =========================
          Main Case Study
          ========================= */}

      <section className="case-study-main-section">
        <div className="case-study-main-grid">

          {/* Left Information */}
          <div className="case-study-information">

            {hasOverview && (
              <div className="case-study-info-block">
                <span className="case-study-label">
                  Project Overview
                </span>
                <p>{caseStudy.description}</p>
              </div>
            )}

            {hasRole && (
              <div className="case-study-info-block">
                <span className="case-study-label">
                  My Role
                </span>
                <p>{caseStudy.role}</p>
              </div>
            )}

            {hasDeliverables && (
              <div className="case-study-info-block">
                <span className="case-study-label">
                  Deliverables
                </span>
                <p className="case-study-free-text">
                  {caseStudy.deliverables}
                </p>
              </div>
            )}

            {hasTimeline && (
              <div className="case-study-info-block">
                <span className="case-study-label">
                  Engagement
                </span>
                <p>{caseStudy.timeline}</p>
              </div>
            )}

            {hasTools && (
              <div className="case-study-info-block">
                <span className="case-study-label">
                  Tools
                </span>

                <div className="case-study-tools">
                  {tools.map((tool, index) => (
                    <span key={`${tool}-${index}`}>
                      {tool}
                    </span>
                  ))}
                </div>
              </div>
            )}

          </div>

          {/* Right Images */}
          <div className="case-study-images">

            {caseStudy.coverImage && (
              <div className="case-study-main-image">
                <img
                  src={caseStudy.coverImage}
                  alt={caseStudy.title}
                />
              </div>
            )}

            {gallery.length > 0 && (
              <div className="case-study-gallery">
                {gallery.map((image, index) => (
                  <div
                    className="case-study-gallery-image"
                    key={`${image}-${index}`}
                  >
                    <img
                      src={image}
                      alt={`${caseStudy.title} ${index + 2}`}
                    />
                  </div>
                ))}
              </div>
            )}

          </div>

        </div>
      </section>

      {/* =========================
          Live Project
          ========================= */}

      {hasLiveUrl && (
        <section className="case-study-live-section">
          <div className="case-study-live-content">
            <span className="case-study-label">
              Live Project
            </span>

            <h2>View the Project</h2>

            <p>
              Visit the live project to explore the final result.
            </p>

            <a
              href={caseStudy.liveUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="case-study-live-button"
            >
              Visit Project
              <span>→</span>
            </a>
          </div>
        </section>
      )}

      {/* =========================
          More Case Studies
          ========================= */}

      {otherCaseStudies.length > 0 && (
        <section className="case-study-more-section">
          <div className="case-study-more-inner">

            <span className="case-study-label">
              Explore More
            </span>

            <h2>More Case Studies</h2>

            <div className="case-study-more-grid">
              {otherCaseStudies.map((item) => (
                <Link
                  to={`/case-studies/${item.slug}`}
                  className="case-study-more-card"
                  key={item.id}
                >
                  {item.coverImage && (
                    <div className="case-study-more-image">
                      <img
                        src={item.coverImage}
                        alt={item.title}
                      />
                    </div>
                  )}

                  <div className="case-study-more-content">
                    {item.category && (
                      <span>{item.category}</span>
                    )}

                    <h3>{item.title}</h3>

                    <p>
                      View Case Study <strong>→</strong>
                    </p>
                  </div>
                </Link>
              ))}
            </div>

            <Link
              to="/case-studies"
              className="case-study-back-button"
            >
              View All Case Studies
              <span>→</span>
            </Link>

          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default CaseStudy;